import { DateTime } from 'luxon';

import { Campaign, CampaignConfiguration, CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { transformCampaignApiToCommonFormFields } from 'modules/campaigns/utils/transformApiToForm/transformCampaignApiToCommonFormFields';

import { CommonCampaignFormValues } from '../types';

export const getClonedCampaignFormValues = (
  campaign: Campaign,
  timezone: string,
  venueIds: string[],
): Omit<CommonCampaignFormValues, 'campaignId' | 'agreementTermsAndConditions'> => {
  const { campaignId, agreementTermsAndConditions, ...commonValuesFromApi } = transformCampaignApiToCommonFormFields(
    campaign,
    timezone,
  );

  return {
    ...commonValuesFromApi,
    venueIds: commonValuesFromApi.venueIds.filter(venueId => venueIds.includes(venueId)),
  };
};

const getDefaultFormValues = ({
  configuration,
  currency,
  country,
  timezone,
  campaignType,
  venueIds,
}: {
  configuration: CampaignConfiguration;
  currency: Currency;
  country: Country;
  timezone: string;
  campaignType: CampaignType | null | undefined;
  venueIds: string[];
}): CommonCampaignFormValues => ({
  agreementTermsAndConditions: false,
  totalBudgetSum: convertMonetaryIntToDecimal(configuration.default_total_budget_sum || 100000, currency),
  firstPurchaseOnly: false,
  startDate:
    country === 'ISR'
      ? DateTime.now()
          .set({
            hour: 0,
            minute: 1,
            second: 0,
            millisecond: 0,
          })
          .setZone(timezone)
          .plus({ day: 7 })
          .toISO()
      : DateTime.now().setZone(timezone).toISO(),
  endDate: DateTime.now()
    .set({
      hour: 23,
      minute: 59,
      second: 0,
      millisecond: 0,
    })
    .setZone(timezone)
    .plus({ day: 21 })
    .toISO(),
  weeklySchedule: null,
  usageType: 'indefinitely',
  minBasketSize: Number(
    Object.keys(
      configuration[campaignType === CampaignType.BASKET_DISCOUNT ? 'basket_discounts' : 'delivery_discounts'],
    )[0],
  ),
  perUserUsageLimit: undefined,
  venueIds,
});

export const getCommonCampaignFormValues = ({
  campaign,
  configuration,
  currency,
  country,
  timezone,
  campaignType,
  venueIds,
}: {
  campaign: Campaign | undefined;
  configuration: CampaignConfiguration;
  currency: Currency;
  country: Country;
  timezone: string;
  campaignType: CampaignType | null | undefined;
  venueIds: string[];
}): CommonCampaignFormValues => {
  const campaignApiFormValues = campaign ? getClonedCampaignFormValues(campaign, timezone, venueIds) : null;

  const defaultFormValues = getDefaultFormValues({
    configuration,
    currency,
    country,
    timezone,
    campaignType,
    venueIds,
  });

  return {
    ...defaultFormValues,
    ...campaignApiFormValues,
  };
};
